import { saveAs } from 'file-saver'
import {
  getDocumentAccounts,
  getDocuments,
  downloadDocumentExcel,
  downloadItemExcel,
} from '@/services/api/LF/lf-api-helper'
import {
  DOCUMENT_ACCOUNTS,
  NET_RCLM_AWAITING_DOCS,
  DOC_COUNT,
  TRANS_COUNT,
  EVENT_COUNT,
  CLIENT_STATUS_GROUPS,
  COUNTRY_GRUOPS,
  FI_ACCT_GROUPS,
  IS_LOADING,
  DOCUMENT_EXPORT_LOADING,
  TRANSACTION_EXPORT_LOADING,
  REPORT_LOADING,
  PRE_FILTERS,
  GROUP_BY,
  CLIENT_DOCS_ONLY,
  API_ERROR_OCCURRED,
  SET_DOCUMENT_ACCOUNTS,
  SET_DOCUMENTS,
  SET_IS_LOADING,
  SET_DOCUMENT_EXPORT_LOADING,
  SET_TRANSACTION_EXPORT_LOADING,
  SET_PRE_FILTERS,
  SET_GROUP_BY,
  SET_CLIENT_DOCS_ONLY,
  SET_ERROR_OCCURRED,
  UPDATE_DOCUMENT_ACCOUNTS,
  UPDATE_DOCUMENTS,
  UPDATE_PRE_FILTERS,
  UPDATE_GROUP_BY,
  UPDATE_CLIENT_DOCS_ONLY,
  DOWNLOAD_DOCUMENT_EXCEL,
  DOWNLOAD_ITEM_EXCEL,
  GROUP_BY_OPTIONS,
} from '../document-management-types'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})
const commaFormatter = new Intl.NumberFormat('en-US')

const formatCurrency = (n) => currencyFormatter.format(n)

const formatComma = (n) => commaFormatter.format(n)

const defaultPreFilters = {
  accounts: [],
  fis: [],
  statuses: [],
  coi: [],
  returnRoute: null,
}

const state = {
  documentAccounts: [],
  summaryStats: {
    netRclmAwaitingDocs: null,
    docCount: null,
    transCount: null,
    eventCount: null,
  },
  clientFacingStatGroups: null,
  countryGroups: null,
  fiAcctGroups: null,
  availableMarkets: null,
  isLoading: false,
  documentExportLoading: false,
  transactionExportLoading: false,
  groupBy: null, // DEFAULT - GROUP_BY_OPTIONS.BO_ACCT_FI_COI.name is assigned on the page
  clientDocsOnly: true,
  apiErrorOccurred: null,

  // The prefilters are set when an entry point to the Document Management
  // screen is selected. The filter selections from the screen with the
  // entry point will be saved here.
  preFilters: defaultPreFilters,
}

const VIEW_AS_GETTER_MODULE_PATH = 'reports/viewAsUserIdJSON'

const getters = {
  [DOCUMENT_ACCOUNTS](state) {
    return state.documentAccounts
  },
  [NET_RCLM_AWAITING_DOCS](state) {
    if (state.summaryStats)
      return formatCurrency(state.summaryStats.netRclmAwaitingDocs)
    else return null
  },
  [DOC_COUNT](state) {
    if (state.summaryStats) return formatComma(state.summaryStats.docCount)
    else return null
  },
  [TRANS_COUNT](state) {
    if (state.summaryStats) return formatComma(state.summaryStats.transCount)
    else return null
  },
  [EVENT_COUNT](state) {
    if (state.summaryStats) return formatComma(state.summaryStats.eventCount)
    else return null
  },
  [CLIENT_STATUS_GROUPS](state) {
    if (state.clientFacingStatGroups)
      return state.clientFacingStatGroups.map((g) => {
        return {
          arg: g.status,
          val: g.pct,
          netReclaim: g.netReclaim,
          itemCount: g.transCount,
          acctCount: g.acctCount,
          key: g.status,
        }
      })
    else return []
  },
  [COUNTRY_GRUOPS](state) {
    if (state.countryGroups)
      return state.countryGroups.map((g) => {
        return {
          arg: g.coiName,
          val: g.pct,
          netReclaim: g.netReclaim,
          itemCount: g.transCount,
          acctCount: g.acctCount,
          key: g.status,
        }
      })
    else return []
  },
  [FI_ACCT_GROUPS](state) {
    if (state.fiAcctGroups)
      return state.fiAcctGroups.map((g) => {
        return {
          acct: g.acctCode,
          inst: g.fiCode,
          fiName: g.fiName,
          bo: g.boName,
          netRclm: g.netReclaim,
          numTransactions: g.transCount,
        }
      })
    else return []
  },
  [IS_LOADING](state) {
    return state.isLoading
  },
  [DOCUMENT_EXPORT_LOADING](state) {
    return state.documentExportLoading
  },
  [TRANSACTION_EXPORT_LOADING](state) {
    return state.transactionExportLoading
  },
  [REPORT_LOADING](state) {
    return state.documentExportLoading || state.transactionExportLoading
  },
  [PRE_FILTERS](state) {
    return state.preFilters
  },
  [GROUP_BY](state) {
    return state.groupBy
  },
  [CLIENT_DOCS_ONLY](state) {
    return state.clientDocsOnly
  },
  [API_ERROR_OCCURRED](state) {
    return state.apiErrorOccurred
  },
}

const mutations = {
  [SET_DOCUMENT_ACCOUNTS](state, payload) {
    state.documentAccounts = payload.documentAccounts
    state.summaryStats = {
      netRclmAwaitingDocs: payload.reclaimValue,
      docCount: payload.documentCount,
      transCount: payload.transactionCount,
      eventCount: payload.eventCount,
    }

    state.clientFacingStatGroups = payload.netMidasStatusSum
    state.countryGroups = payload.coiNetReclaimSum
    state.fiAcctGroups = payload.fiAcctTransactionSum
  },
  [SET_DOCUMENTS](state, payload) {
    // Find the document group to assign the documents
    // to. The criteria used to identify the courrect
    // document group is based on what the document
    // grid is currently grouped by
    switch (state.groupBy) {
      case GROUP_BY_OPTIONS.BO_ACCT_FI_COI.name:
        state.documentAccounts.forEach((a) => {
          if (
            a.fi_code === payload.key.custodian &&
            a.accountNumber === payload.key.accountNumber &&
            a.coi_name === payload.key.coi
          ) {
            a.documents = payload.data.allDocs
            a.docsPerSubInvestor = payload.data.docsPerInvestor
            a.subInvestors = payload.data.investors
          }
        })
        break
      case GROUP_BY_OPTIONS.BO.name:
        state.documentAccounts.forEach((a) => {
          if (a.fund_boclient_id === payload.key.fundBOClientId) {
            a.documents = payload.data.allDocs
            a.docsPerSubInvestor = payload.data.docsPerInvestor
            a.subInvestors = payload.data.investors
          }
        })
        break
      case GROUP_BY_OPTIONS.BO_ACCT.name:
        state.documentAccounts.forEach((a) => {
          if (
            a.fi_code === payload.key.custodian &&
            a.accountNumber === payload.key.accountNumber
          ) {
            a.documents = payload.data.allDocs
            a.docsPerSubInvestor = payload.data.docsPerInvestor
            a.subInvestors = payload.data.investors
          }
        })
        break
      case GROUP_BY_OPTIONS.BO_COI.name:
        state.documentAccounts.forEach((a) => {
          if (
            a.fund_boclient_id === payload.key.fundBOClientId &&
            a.coi_name === payload.key.coi
          ) {
            a.documents = payload.data.allDocs
            a.docsPerSubInvestor = payload.data.docsPerInvestor
            a.subInvestors = payload.data.investors
          }
        })
        break
      case GROUP_BY_OPTIONS.SUB_INVESTOR.name:
        state.documentAccounts.forEach((a) => {
          if (a.boclient_id === payload.key.boClientId) {
            a.documents = payload.data.allDocs
            a.docsPerFund = payload.data.docsPerFund
            a.funds = payload.data.funds
          }
        })
        break
    }
  },
  [SET_IS_LOADING](state, payload) {
    state.isLoading = payload
  },
  [SET_DOCUMENT_EXPORT_LOADING](state, payload) {
    state.documentExportLoading = payload
  },
  [SET_TRANSACTION_EXPORT_LOADING](state, payload) {
    state.transactionExportLoading = payload
  },
  [SET_PRE_FILTERS](state, payload) {
    state.preFilters = payload
  },
  [SET_GROUP_BY](state, payload) {
    state.groupBy = payload
  },
  [SET_CLIENT_DOCS_ONLY](state, payload) {
    state.clientDocsOnly = payload
  },
  [SET_ERROR_OCCURRED](state, payload) {
    state.apiErrorOccurred = payload
  },
}

const actions = {
  [UPDATE_DOCUMENT_ACCOUNTS]({ commit, state, rootGetters }, payload) {
    if (payload === null) {
      commit(SET_DOCUMENT_ACCOUNTS, [])
      return
    }

    commit(SET_IS_LOADING, true)
    let filterParameters = {
      clientFacingStatus: payload?.clientFacingStatuses[0] ?? '',
      coi: payload?.countryNames[0] ?? '',
      //fiAccts: payload?.fiAccts ?? [],
      custodian: payload.fiCodes.join(),
      accountNumber: payload.acctCodes.join(),
      groupBy: state.groupBy,
      clientDocsOnly: state.clientDocsOnly,
    }

    Object.assign(filterParameters, rootGetters[VIEW_AS_GETTER_MODULE_PATH])

    commit(SET_ERROR_OCCURRED, null)

    // if (filterParameters.groupBy == 'sub-investor') {
    //   commit(SET_DOCUMENT_ACCOUNTS, demoDocInvestorAccounts)
    //   commit(SET_IS_LOADING, false)
    // } else {
    //   commit(SET_DOCUMENT_ACCOUNTS, demoDocAccounts)
    //   commit(SET_IS_LOADING, false)
    // }

    getDocumentAccounts(
      filterParameters,
      (data) => {
        commit(SET_DOCUMENT_ACCOUNTS, data)
        commit(SET_IS_LOADING, false)
      },
      (jqXHR) => {
        commit(SET_DOCUMENT_ACCOUNTS, [])
        commit(SET_IS_LOADING, false)
        commit(SET_ERROR_OCCURRED, jqXHR)
      },
    )
  },
  [UPDATE_DOCUMENTS]({ commit, state, rootGetters }, payload) {
    // filterParameters.groupBy = state.groupBy
    // filterParameters.clientDocsOnly = state.clientDocsOnly
    // filterParameters.clientFacingStatus =
    //   filterParameters.clientFacingStatuses[0] ?? ''
    // filterParameters.coi = filterParameters.countryNames[0] ?? ''
    // filterParameters.custodian = filterParameters.fiCodes.join()
    // filterParameters.accountNumber = filterParameters.acctCodes.join()

    let filterParameters = {
      groupBy: state.groupBy,
      clientDocsOnly: state.clientDocsOnly,
      clientFacingStatus: payload.clientFacingStatuses[0] ?? '',
      coi: payload.countryNames[0] ?? '',
      custodian: payload.fiCodes.join(),
      accountNumber: payload.acctCodes.join(),
      fundBOClientId: payload.fundBOClientId,
      boClientId: payload.boClientId,
    }

    Object.assign(
      filterParameters || {},
      rootGetters[VIEW_AS_GETTER_MODULE_PATH],
    )

    commit(SET_ERROR_OCCURRED, null)

    getDocuments(
      filterParameters,
      (data) => {
        commit(SET_DOCUMENTS, {
          key: filterParameters,
          data: data,
        })
      },
      (jqXHR) => {
        commit(SET_ERROR_OCCURRED, jqXHR)
      },
    )
  },
  [UPDATE_PRE_FILTERS]({ commit }, preFilters) {
    if (preFilters) commit(SET_PRE_FILTERS, preFilters)
    else commit(SET_PRE_FILTERS, defaultPreFilters)
  },
  [UPDATE_GROUP_BY]({ commit }, groupBy) {
    commit(SET_GROUP_BY, groupBy)
  },
  [UPDATE_CLIENT_DOCS_ONLY]({ commit }, clientDocsOnly) {
    commit(SET_CLIENT_DOCS_ONLY, clientDocsOnly)
  },
  [DOWNLOAD_DOCUMENT_EXCEL]({ commit, state, rootGetters }, filterParameters) {
    // The viewReportAs.userId from the vuex reports module enables
    // admin users to emulate other users/clients
    // This action accepts a reportGuid which represents the report
    // to download. The filter parameters object is created which
    // encapsulates the reportGuid and the userId to emulate
    commit(SET_DOCUMENT_EXPORT_LOADING, true)

    filterParameters.clientDocsOnly = state.clientDocsOnly
    filterParameters.fiAccts = []

    Object.assign(
      filterParameters || {},
      rootGetters[VIEW_AS_GETTER_MODULE_PATH],
    )

    commit(SET_ERROR_OCCURRED, null)

    downloadDocumentExcel(
      filterParameters,
      (data, filename) => {
        let blob = b64toBlob(data.file_binary, data.file_extension)
        filename = `${data.file_name}`
        saveAs(blob, filename)
        commit(SET_DOCUMENT_EXPORT_LOADING, false)
      },
      (jqXHR) => {
        commit(SET_DOCUMENT_EXPORT_LOADING, false)
        commit(SET_ERROR_OCCURRED, jqXHR)
      },
    )
  },
  [DOWNLOAD_ITEM_EXCEL]({ commit, state, rootGetters }, filterParameters) {
    commit(SET_TRANSACTION_EXPORT_LOADING, true)

    filterParameters.clientDocsOnly = state.clientDocsOnly
    filterParameters.fiAccts = []

    Object.assign(
      filterParameters || {},
      rootGetters[VIEW_AS_GETTER_MODULE_PATH],
    )

    commit(SET_ERROR_OCCURRED, null)

    downloadItemExcel(
      filterParameters,
      (data, filename) => {
        let blob = b64toBlob(data.file_binary, data.file_extension)
        filename = `${data.file_name}`
        saveAs(blob, filename)
        commit(SET_TRANSACTION_EXPORT_LOADING, false)
      },
      (jqXHR) => {
        commit(SET_TRANSACTION_EXPORT_LOADING, false)
        commit(SET_ERROR_OCCURRED, jqXHR)
      },
    )
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}

const contentTypes = {
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xls': 'application/vnd.ms-excel',
  '.pdf': 'application/pdf',
}

// helper methods
const b64toBlob = (b64Data, fileExtension = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data)
  const byteArrays = []

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize)

    const byteNumbers = new Array(slice.length)
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)
    byteArrays.push(byteArray)
  }

  return new Blob(byteArrays, { type: contentTypes[fileExtension] })
}
